import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw,
  Wifi,
  WifiOff,
  Bell,
  BellOff,
  Settings,
  Maximize2,
  Minimize2,
  ChefHat,
  Clock,
} from 'lucide-react';
import { KDSOrderCard } from '../components/kds/KDSOrderCard';
import { StationTabs } from '../components/kds/StationTabs';
import { kdsApi } from '../lib/api/kds';
import { useKDSWebSocket } from '../hooks/useKDSWebSocket';
import { KDSOrder, KitchenStation, OrderStatus, OrderPriority } from '../types/kds';
import { playNewOrder, playOrderReady } from '../utils/sounds';
import { celebrateOrderComplete } from '../utils/confetti';

export function KDSPage() {
  const [orders, setOrders] = useState<KDSOrder[]>([]);
  const [stations, setStations] = useState<KitchenStation[]>([]);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);

  // Get restaurant ID from auth (you may need to adjust this based on your auth system)
  const restaurantId = localStorage.getItem('restaurantId') || '00f56cae-a843-48bd-923d-9c32909d4fb9';

  // Load orders
  const loadOrders = useCallback(async () => {
    try {
      console.log('🔄 Loading KDS orders...');
      const data = selectedStationId
        ? await kdsApi.getOrdersByStation(selectedStationId)
        : await kdsApi.getActiveOrders();

      console.log('✅ Orders loaded:', data);

      // Check for new orders (play sound)
      if (orders.length > 0 && data.length > orders.length) {
        const newOrdersCount = data.length - orders.length;
        console.log(`🆕 ${newOrdersCount} new order(s) detected`);
        if (soundEnabled) {
          playNewOrder();
        }
      }

      setOrders(data);
      setLastUpdate(new Date());
      setIsConnected(true);
    } catch (error: any) {
      console.error('❌ Failed to load orders:', error);
      console.error('Error details:', error?.response?.data || error?.message);
      setIsConnected(false);
      setOrders([]); // Clear orders on error
    }
  }, [selectedStationId, orders.length, soundEnabled]);

  // Load stations
  const loadStations = useCallback(async () => {
    try {
      const data = await kdsApi.getActiveStations();
      setStations(data);
    } catch (error) {
      console.error('Failed to load stations:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadOrders(), loadStations()]);
      setIsLoading(false);
    };
    init();
  }, [loadOrders, loadStations]);

  // WebSocket connection (optional - will work without it with polling)
  // Disabled for now - use polling instead
  // useKDSWebSocket(restaurantId, loadOrders);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(loadOrders, 10000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  // Bump order (move to next status)
  const handleBump = useCallback(
    async (orderId: string) => {
      console.log('🔄 Bumping order:', orderId);
      setProcessingOrderId(orderId);

      try {
        await kdsApi.bumpOrder(orderId);
        console.log('✅ Order bumped successfully');

        // Celebrate order completion
        celebrateOrderComplete();
        playOrderReady();

        await loadOrders();
      } catch (error) {
        console.error('❌ Failed to bump order:', error);
        const errorMsg = (error as any)?.response?.data?.message || (error as any)?.message || 'Errore sconosciuto';
        alert('Errore durante il bump dell\'ordine: ' + errorMsg);
      } finally {
        setProcessingOrderId(null);
      }
    },
    [loadOrders]
  );

  // Update priority
  const handleUpdatePriority = useCallback(
    async (orderId: string, priority: OrderPriority) => {
      try {
        await kdsApi.updateOrderPriority(orderId, { priority });
        await loadOrders();
      } catch (error) {
        console.error('Failed to update priority:', error);
      }
    },
    [loadOrders]
  );

  // Toggle item status
  const handleItemToggle = useCallback(
    async (itemId: string) => {
      try {
        // Toggle between PENDING/PREPARING/READY
        // This is simplified - in production you'd check current status
        await kdsApi.updateItemStatus(itemId, {
          status: 'PREPARING' as any,
          preparedBy: 'Staff',
        });
        await loadOrders();
      } catch (error) {
        console.error('Failed to update item:', error);
      }
    },
    [loadOrders]
  );

  // Calculate order counts per station
  const orderCounts = useMemo(() => {
    const counts: Record<string, number> = {
      total: orders?.length || 0,
    };

    if (stations && Array.isArray(stations)) {
      stations.forEach((station) => {
        if (station?.id) {
          counts[station.id] = orders?.filter((order) =>
            order?.items?.some((item) => item?.kitchenStationId === station.id)
          ).length || 0;
        }
      });
    }

    return counts;
  }, [orders, stations]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Group orders by status
  const groupedOrders = useMemo(() => {
    if (!orders || orders.length === 0) {
      return { submitted: [], preparing: [], ready: [] };
    }

    const submitted = orders.filter((o) => o?.status === 'SUBMITTED');
    const preparing = orders.filter((o) => o?.status === 'PREPARING');
    const ready = orders.filter((o) => o?.status === 'READY');

    return { submitted, preparing, ready };
  }, [orders]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1f] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-gray-500 dark:text-white/40">Caricamento KDS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1f]">
      {/* Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-[#1e1e24]/90 border-b border-gray-200 dark:border-white/[0.08] shadow-sm">
        <div className="px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            {/* Title */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  Kitchen Display
                </h1>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-white/40 mt-0.5">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Ultimo aggiornamento: {lastUpdate.toLocaleTimeString()}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Connection status */}
              <motion.div
                animate={{ scale: isConnected ? 1 : [1, 1.1, 1] }}
                transition={{ repeat: isConnected ? 0 : Infinity, duration: 1 }}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl font-medium text-sm
                  ${
                    isConnected
                      ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                      : 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'
                  }`}
              >
                {isConnected ? (
                  <Wifi className="w-4 h-4" />
                ) : (
                  <WifiOff className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{isConnected ? 'Connesso' : 'Disconnesso'}</span>
              </motion.div>

              {/* Sound toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-2.5 rounded-xl transition-colors
                  ${
                    soundEnabled
                      ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                      : 'bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-white/40'
                  }`}
              >
                {soundEnabled ? (
                  <Bell className="w-5 h-5" />
                ) : (
                  <BellOff className="w-5 h-5" />
                )}
              </motion.button>

              {/* Refresh */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95, rotate: 180 }}
                onClick={loadOrders}
                className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition"
              >
                <RefreshCw className="w-5 h-5" />
              </motion.button>

              {/* Fullscreen */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleFullscreen}
                className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition"
              >
                {isFullscreen ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </motion.button>

              {/* Settings */}
              <motion.button
                whileHover={{ scale: 1.05, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
                className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition"
              >
                <Settings className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Station tabs */}
          <StationTabs
            stations={stations}
            selectedStationId={selectedStationId}
            onSelectStation={setSelectedStationId}
            orderCounts={orderCounts}
          />
        </div>
      </div>

      {/* Stats Bar */}
      <div className="px-4 md:px-6 py-4 bg-white dark:bg-[#25252d] border-b border-gray-200 dark:border-white/[0.08]">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-bold">{groupedOrders.submitted.length}</span>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-white/40">Nuovi</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">In attesa</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center">
              <span className="text-yellow-600 dark:text-yellow-400 font-bold">{groupedOrders.preparing.length}</span>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-white/40">In corso</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Preparazione</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <span className="text-emerald-600 dark:text-emerald-400 font-bold">{groupedOrders.ready.length}</span>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-white/40">Pronti</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Da servire</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-white/[0.04] rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
              <span className="text-orange-600 dark:text-orange-400 font-bold">{orders.length}</span>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-white/40">Totale</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Ordini attivi</div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders grid */}
      <div className="p-4 md:p-6">
        {!isConnected ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-12 text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <WifiOff className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Errore di connessione
            </h2>
            <p className="text-gray-500 dark:text-white/40 mb-6">
              Impossibile caricare gli ordini. Verifica di essere autenticato.
            </p>
            <button
              onClick={loadOrders}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold transition-colors"
            >
              Riprova
            </button>
          </motion.div>
        ) : orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-12 text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
              <ChefHat className="w-8 h-8 text-gray-400 dark:text-white/30" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Nessun ordine attivo
            </h2>
            <p className="text-gray-500 dark:text-white/40">
              Gli ordini appariranno qui quando arrivano
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            <AnimatePresence mode="popLayout">
              {/* Submitted orders first (new) */}
              {groupedOrders.submitted.map((order) => (
                <KDSOrderCard
                  key={order.id}
                  order={order}
                  onBump={handleBump}
                  onUpdatePriority={handleUpdatePriority}
                  onItemToggle={handleItemToggle}
                />
              ))}

              {/* Then preparing orders */}
              {groupedOrders.preparing.map((order) => (
                <KDSOrderCard
                  key={order.id}
                  order={order}
                  onBump={handleBump}
                  onUpdatePriority={handleUpdatePriority}
                  onItemToggle={handleItemToggle}
                />
              ))}

              {/* Finally ready orders */}
              {groupedOrders.ready.map((order) => (
                <KDSOrderCard
                  key={order.id}
                  order={order}
                  onBump={handleBump}
                  onUpdatePriority={handleUpdatePriority}
                  onItemToggle={handleItemToggle}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.15);
          border-radius: 4px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0,0,0,0.25);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>
    </div>
  );
}
